import { useEffect, useMemo, useState } from "react";
import { Minus, Plus, X } from "@phosphor-icons/react";
import { Sheet, SheetContent } from "@shopflo/ui/sheet";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import Price from "components/common/Price";
import { staticOptions } from "lib/core/apiClient";
import { useUpsellValues } from "lib/hooks/useUpsellValues";
import { UpsellPositionType, UpSellProductCardProps, UpSellVariantType } from "lib/types/upsell";
import { classNames } from "lib/utils/helpers";
import useSWR from "swr";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { Icon } from "@shopflo/ui/icon";
import { analyticsEvents, eventTypes, FALLBACK_PRODUCT_IMAGE } from "lib/utils/constants";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import FullPageCarousel from "@shopflo/ui/full-page-carousel";
import { FullCardCarousel } from "@shopflo/ui/full-card-carousel";
import { CarouselItem } from "@shopflo/ui/carousel";

interface UpsellBottomsheetProps {
  open: boolean;
  onClose: () => void;
  upsellProduct: UpSellProductCardProps;
  selectedVariant: UpSellVariantType | null;
  handleVariantChange: () => void;
  position: UpsellPositionType;
}

const UpsellBottomsheet: React.FC<UpsellBottomsheetProps> = ({
  open,
  onClose,
  upsellProduct,
  selectedVariant,
  handleVariantChange,
  position,
}) => {
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { product, isDiscounted } = upsellProduct;
  const variant = selectedVariant ?? upsellProduct.product.variants?.[0];
  const showDiscountPrice = isDiscounted && Boolean(variant?.discountedPrice);
  const productPrice = showDiscountPrice ? variant.discountedPrice : variant?.currentPrice;

  const [isFullPageCarouselOpen, setIsFullPageCarouselOpen] = useState(false);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

  const images = useMemo(() => {
    if (upsellProduct.product.variants.length > 1) {
      return variant?.imageLink ? [variant?.imageLink] : [FALLBACK_PRODUCT_IMAGE];
    }
    return upsellProduct.product.images?.length ? upsellProduct.product.images : [FALLBACK_PRODUCT_IMAGE];
  }, [upsellProduct, variant]);

  const { submitSelection, itemsData, isLoading, percentageDiscount, getCompareAtPrice, handleEditProduct } =
    useUpsellValues({
      upsellProduct,
    });

  const triggerAnalyticsEvent = (event: string) => {
    sendAnalyticsEvent({
      eventName: event,
      eventType: "click",
      metaData: {
        upsellData: {
          position,
          productId: product.productId,
          UpSellSection: "generic",
          groupIds: product.upsellIds,
          variantId: variant?.variantId,
        },
      },
    });
  };

  // Handlers for full-page carousel
  const handleImageClick = () => {
    setIsFullPageCarouselOpen(true);
    triggerAnalyticsEvent(analyticsEvents.UPSELL_PREVIEW_IMAGE_CLICKED);
  };

  const closeFullPageCarousel = () => {
    setIsFullPageCarouselOpen(false);
  };

  const handleCarouselIndexChange = (index: number) => {
    setCurrentCarouselIndex(index);
  };

  const {
    state: { merchant },
  } = useMerchantContext();

  const { data: productResponse, isValidating } = useSWR(
    open && product?.productId ? `fetch-product-description-${product.productId}` : null,
    async () => {
      const path = `https://${merchant?.shopData?.domain}/products/${product?.description}.js`;
      const data = await fetch(path);
      const parsedData = await data?.json();
      return parsedData;
    },
    staticOptions,
  );

  const hasVariants = upsellProduct?.product.variants.length > 1;
  const itemQty = itemsData[variant?.variantId] ?? 1;
  const isAddUnavailable = variant?.inventoryPolicy === "DENY" && variant?.inventoryQuantity <= itemQty;

  const counterNode = (
    <div className="border border-gray-light rounded-lg flex justify-between items-center px-2 py-3 min-w-28">
      <Minus
        className={classNames("h-5 w-5 cursor-pointer", itemQty <= 1 ? "text-gray-light" : "text-coal-dark")}
        onClick={() => {
          handleEditProduct(variant?.variantId, itemQty - 1);
        }}
      />
      {itemQty}
      <Plus
        className={classNames(
          "h-5 w-5 cursor-pointer",
          isAddUnavailable ? "text-gray-light" : "text-coal-dark",
        )}
        onClick={() => {
          if (isAddUnavailable) return;
          handleEditProduct(variant?.variantId, itemQty + 1);
        }}
      />
    </div>
  );

  const productHeaderNode = (
    <div className={classNames("flex flex-col gap-2", !hasVariants ? "mb-3" : "")}>
      <div className="flex justify-between gap-4">
        <div className="flex flex-col gap-2">
          <div className="text-base font-medium">{product.name}</div>
          <div className="text-xs text-gray-dark">{variant.name}</div>
        </div>
        <div className="flex flex-col justify-center gap-2">
          {percentageDiscount(variant, showDiscountPrice) > 0 && (
            <div className="h-6 bg-yay-dark text-white text-xs font-semibold flex justify-center items-center px-2 py-1 rounded-sm">
              {Math.round(percentageDiscount(variant, showDiscountPrice))}% off
            </div>
          )}
          <Price
            total={productPrice ?? 0}
            compareAt={getCompareAtPrice(variant, showDiscountPrice)}
            orientation="horizontal"
            size="md"
          />
        </div>
      </div>
    </div>
  );

  const productHtmlContentNode = (
    <div
      className="text-[12px] text-gray-dark [&_tr]:!h-7 [&_tr]:border-b [&_tr]:border-gray-light [&_th]:bg-gray-light [&_th]:px-2"
      dangerouslySetInnerHTML={{
        __html: productResponse?.description || product?.description || "",
      }}></div>
  );

  const moreOptionsNode = (
    <div className="my-3">
      <button
        onClick={() => {
          handleVariantChange();
          triggerAnalyticsEvent(analyticsEvents.UPSELL_PREVIEW_VARIANT_SELCTOR_CLICKED);
        }}
        className="flex items-center justify-center space-x-1 rounded-lg border-2 border-yay-dark p-2 text-sm font-medium text-yay-dark w-full">
        <span>+{product?.variants?.length - 1} more options</span>
      </button>
    </div>
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side="bottom" withinContainer container={document.getElementById("checkout-ui-root")}>
          {isLoading && <OverlaySpinner />}
          <div className="flex flex-col h-full max-h-[80vh]">
            <div className="overflow-y-auto scrollbar-hide">
              <div className="cursor-pointer relative" onClick={handleImageClick}>
                <FullCardCarousel images={images} onIndexChange={handleCarouselIndexChange} />
                <div
                  className="absolute right-2 top-2 rounded-lg bg-black/10 p-1"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onClose();
                  }}>
                  <Icon IconComponent={X} size="xs" iconProps={{ color: "white" }} />
                </div>
              </div>
              <div className="px-4 py-3">
                {productHeaderNode}
                {hasVariants && moreOptionsNode}
                {productHtmlContentNode}
              </div>
            </div>
            <div className="p-3 border border-gray-light flex gap-2 items-center">
              <div>{counterNode}</div>
              <PrimaryButton
                onClick={() => {
                  const payload = {
                    [variant?.variantId]: itemQty,
                  };
                  submitSelection({ itemsObj: payload });
                }}
                isDisabled={variant?.inventoryQuantity < 1 && variant?.inventoryPolicy === "DENY"}
                buttonText={"Proceed"}
                isLoading={isLoading}
              />
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <FullPageCarousel
        isOpen={isFullPageCarouselOpen}
        onClose={closeFullPageCarousel}
        totalSlides={images.length}
        startIndex={currentCarouselIndex}
      >
        {images.map((image, index) => (
          <CarouselItem key={index} className="flex items-center">
            <div className="ui-h-full ui-w-full ui-flex ui-items-center ui-justify-center">
              <img
                src={image}
                alt={`${product.name} - Image ${index + 1}`}
                className="ui-object-contain ui-max-h-full ui-max-w-full"
              />
            </div>
          </CarouselItem>
        ))}
      </FullPageCarousel>
    </>
  );
};

export default UpsellBottomsheet;
